import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { Form, Button } from "react-bootstrap";
import Loader from "./Loader.js";

/**
 * Reusable upload function (so NewVehicle can upload after create).
 */
export const uploadAttachment = async ({
  entityId,
  type,
  file,
  name,
  description,
}) => {
  if (!entityId) throw new Error("entityId is required to upload");
  if (!file) throw new Error("file is required to upload");

  const formData = new FormData();
  formData.append("file", file);
  formData.append("type", type);
  formData.append("id", entityId);
  formData.append("name", name ?? "");
  formData.append("description", description ?? "");

  const baseUrl = window.location.origin;

  const response = await axios.post(`${baseUrl}/api/Attachments/${entityId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    withCredentials: false,
  });

  return response.data;
};

/**
 * Props:
 * - autoUpload (default true): existing behavior, uploads immediately on selection.
 * - onFileSelected: called when a file is picked (especially useful in deferred mode)
 * - showPreview (default false): optional inline preview
 */
const Upload = ({
  type,
  entityId,
  name,
  description,
  buttonLabel = "Upload",
  onComplete,
  autoUpload = true,
  onFileSelected,
  showPreview = false,
}) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);

    // Preview is optional
    if (showPreview) {
      if (preview) URL.revokeObjectURL(preview);
      setPreview(URL.createObjectURL(selectedFile));
    }

    // Always notify parent (lets NewVehicle store it)
    if (onFileSelected) onFileSelected(selectedFile);

    // Deferred mode OR missing entityId: stop here (no upload yet)
    if (!autoUpload || !entityId) {
      return;
    }

    // Existing behavior: upload immediately
    setUploading(true);
    try {
      const data = await uploadAttachment({
        entityId,
        type,
        file: selectedFile,
        name,
        description,
      });

      console.log("Uploaded URL:", data?.Url ?? data?.url ?? data);
      if (onComplete) onComplete();
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="container text-center mb-3">
      <Form.Group controlId="fileUpload">
        {uploading && <Loader />}

        <Form.Control
          type="file"
          onChange={handleFileChange}
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
        />

        <Button onClick={handleUploadClick} disabled={uploading}>
          {buttonLabel}
        </Button>
      </Form.Group>

      {showPreview && preview && (
        <div className="text-center mt-3">
          <img
            src={preview}
            alt="Preview"
            className="img-thumbnail"
            style={{ maxWidth: "200px" }}
          />
        </div>
      )}
    </div>
  );
};

export default Upload;
